import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import axios from "axios";
import { Heart, Sparkle, Envelope, Users, Baby } from "@phosphor-icons/react";

export default function SharedJourney() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/share/journey/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Link not found"));
  }, [code]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-vansha p-8" data-testid="shared-error">
      <div className="text-center max-w-md">
        <p className="overline text-saffron mb-3">Vansha</p>
        <h1 className="font-serif text-4xl mb-4">{err}</h1>
        <p className="text-[#3D2E5C] mb-6">The family may have revoked this link, or the code is incorrect.</p>
        <Link to="/" className="btn-primary">Learn about Vansha</Link>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#7A6E96]">Loading…</div>;

  const status = data.pregnancy_status;
  const photoUrl = (id) => `${API_BASE}/share/journey/${code}/photo/${id}`;

  return (
    <div className="min-h-screen bg-vansha" data-testid="shared-journey-page">
      <header className="glass-header sticky top-0 z-40">
        <div className="vansha-container flex items-center justify-between h-16">
          <Link to="/" className="font-serif text-2xl text-[#1B1533]">Vansha</Link>
          <Link to="/register" className="btn-primary text-sm" data-testid="shared-cta-register">Begin your own journey</Link>
        </div>
      </header>

      <section className="vansha-container py-14 sm:py-20 max-w-4xl">
        <p className="overline text-saffron mb-4" data-testid="shared-overline">A family's story · shared</p>
        <h1 className="font-serif font-semibold text-5xl sm:text-6xl leading-tight text-[#1B1533]" data-testid="shared-title">
          {data.family_name}<br />
          <em className="text-terracotta">is expecting</em>.
        </h1>
        {status && (
          <p className="mt-6 text-lg text-[#3D2E5C] max-w-2xl">
            {data.owner_name} is in <b>week {status.current_week}</b>, trimester {status.trimester}.
            {status.days_to_due > 0 ? ` ${status.days_to_due} days to meet the baby.` : " Due date has arrived."}
          </p>
        )}
        {data.has_baby && (
          <p className="mt-6 text-lg text-[#3D2E5C]">Welcome, <b>{data.baby_name}</b>.</p>
        )}
      </section>

      {data.memories?.length > 0 && (
        <section className="vansha-container pb-14" data-testid="shared-memories">
          <p className="overline text-saffron mb-4">Moments</p>
          <div className="columns-2 sm:columns-3 gap-3 space-y-3">
            {data.memories.map((m) => (
              <div key={m.id} className="vansha-card overflow-hidden break-inside-avoid mb-3" data-testid={`shared-memory-${m.id}`}>
                <img src={photoUrl(m.id)} alt={m.caption || ""} className="w-full block" loading="lazy" />
                {m.caption && <p className="p-3 font-serif text-sm leading-snug">{m.caption}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {data.timeline?.length > 0 && (
        <section className="vansha-container py-14" data-testid="shared-timeline">
          <p className="overline text-saffron mb-4">Journey so far</p>
          <div className="relative max-w-2xl">
            <div className="absolute left-6 top-2 bottom-2 w-px timeline-line" />
            <div className="space-y-6">
              {data.timeline.map((t) => (
                <div key={t.id} className="relative pl-16" data-testid={`shared-timeline-${t.id}`}>
                  <div className="absolute left-[18px] top-3 timeline-dot" />
                  <div className="vansha-card p-5">
                    <p className="overline text-saffron mb-1">{t.event_date}</p>
                    <h3 className="font-serif text-xl">{t.title}</h3>
                    {t.description && <p className="text-[#3D2E5C] text-sm mt-2">{t.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.letters?.length > 0 && (
        <section className="vansha-container py-14 bg-vansha-surface" data-testid="shared-letters">
          <div className="max-w-4xl mx-auto">
            <p className="overline text-saffron mb-4">Letters written</p>
            <h2 className="font-serif text-3xl mb-8">Words being sealed in time</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.letters.map((l) => (
                <div key={l.id} className="vansha-card p-5" data-testid={`shared-letter-${l.id}`}>
                  <Envelope weight={l.sealed ? "fill" : "thin"} size={20} className={l.sealed ? "text-saffron mb-2" : "text-terracotta mb-2"} />
                  <p className="overline text-saffron">{l.category?.replace("_", " ")}</p>
                  <h3 className="font-serif text-lg mt-1">{l.title}</h3>
                  <p className="text-xs text-[#7A6E96] mt-1">From {l.from_name}</p>
                  {l.sealed && <p className="text-xs text-saffron mt-2 italic">Sealed for the future</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.family_members?.length > 0 && (
        <section className="vansha-container py-14 max-w-4xl" data-testid="shared-family">
          <p className="overline text-saffron mb-4">The circle</p>
          <h2 className="font-serif text-3xl mb-6">Made together, by</h2>
          <div className="flex flex-wrap gap-3">
            {data.family_members.map((m, i) => (
              <div key={i} className="vansha-card px-5 py-3" data-testid={`shared-member-${i}`}>
                <p className="font-serif text-lg">{m.name}</p>
                <p className="overline capitalize">{m.role}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="vansha-container py-20" data-testid="shared-cta">
        <div className="bg-forest text-white rounded-3xl p-10 sm:p-16 max-w-4xl mx-auto">
          <p className="overline mb-4" style={{ color: "#D9C9B4" }}>Vansha</p>
          <h2 className="font-serif text-4xl leading-tight">Your family deserves<br /><em style={{ color: "#C88E4B" }}>the same care.</em></h2>
          <p className="mt-6 text-lg opacity-85 max-w-xl">
            Every photo, every letter, every heartbeat — one place, forever. Begin free.
          </p>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 bg-saffron text-forest hover:bg-[#b87a39] rounded-full px-8 py-4 font-medium transition" data-testid="shared-register-cta">
            <Sparkle weight="thin" size={18} /> Begin your Vansha
          </Link>
        </div>
      </section>

      <footer className="border-t border-ink-10 py-10 text-center text-sm text-[#7A6E96]">
        <p>Vansha · From First Heartbeat to First Steps · vansha.family</p>
      </footer>
    </div>
  );
}
